import { useState } from "react";
import type { FormEvent } from "react";
import { KeyRound } from "lucide-react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { mailbox, mailErrorCode, mailErrorMessage, MAILBOX_BAD_PASSWORD } from "../../api/mailbox";
import type { MailboxStatus } from "../../api/types";

// 啟用信箱 / 重新輸入信箱密碼(規格 §2.2)。
//
// 帳號就是登入帳號(`User.email`),只問密碼。密碼**只寫不讀**:送出後後端先拿它登入郵件主機
// 試一次,過了才加密存起來 —— 錯的密碼當場就說「密碼不對」,不會存下一個註定同步不了的密碼。
//
// 兩種樣子:
// - `setup`:還沒啟用,整頁就是這一塊。
// - `reauth`:密碼在郵件主機上改過了(同步被拒),頁面最上面一條紅條 + 密碼欄。

export interface MailboxSetupProps {
  mode: "setup" | "reauth";
  email?: string;
  onEnabled: (status: MailboxStatus) => void;
}

export function MailboxSetup({ mode, email, onEnabled }: MailboxSetupProps) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    if (password === "") {
      setError("請輸入信箱密碼");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const status = await mailbox.enable(password);
      setPassword("");
      onEnabled(status);
    } catch (err) {
      setError(mailErrorCode(err) === MAILBOX_BAD_PASSWORD ? "密碼不對" : mailErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (
    <Input
      label="信箱密碼"
      type="password"
      autoComplete="current-password"
      value={password}
      error={error}
      errorRole="alert"
      onChange={(e) => setPassword(e.target.value)}
    />
  );

  if (mode === "reauth") {
    return (
      <div
        role="alert"
        className="flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--surface)] px-3 py-2"
      >
        <p className="m-0 text-sm font-medium text-[var(--danger)]">信箱密碼可能改過了,請重新輸入</p>
        <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
          <div className="w-64 max-w-full">{field}</div>
          <Button type="submit" variant="primary" loading={busy} icon={<KeyRound aria-hidden="true" size={16} />}>
            重新輸入
          </Button>
        </form>
      </div>
    );
  }

  return (
    <section
      aria-label="啟用信箱"
      className="flex max-w-lg flex-col gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-4"
    >
      <h3 className="m-0 text-base font-semibold text-[var(--text)]">啟用我的信箱</h3>
      <p className="m-0 text-sm text-[var(--text-2)]">
        輸入{email ? <strong> {email} </strong> : "你的信箱"}的密碼(帳號就是登入用的 Email)。
        啟用後系統會把你的信複製一份備份進來,之後在這裡收信、寫信。只有你自己看得到。
      </p>
      <form onSubmit={submit} className="flex flex-col gap-3">
        {field}
        <div>
          <Button type="submit" variant="primary" loading={busy}>
            啟用
          </Button>
        </div>
      </form>
    </section>
  );
}
